'use client';
import React, { ChangeEvent, forwardRef, useEffect, useRef } from 'react';
import Button from '@pezeshk-book/ui-kit/button';
import Chip from '@pezeshk-book/ui-kit/chip';
import Div from '@pezeshk-book/ui-kit/div';
import Rating from '@pezeshk-book/ui-kit/rating';
import Text from '@pezeshk-book/ui-kit/text';
import TextField from '@pezeshk-book/ui-kit/text-field';
import CommentIcon from '@icons-components/comment-icon';
import { useDispatch } from 'react-redux';
import { AlertActions } from '@store/alert/alert-action';
import { SubmitFormProps } from './submit-form.props';
import { useParams } from 'next/navigation';
import getDictionary from '@dictionaries-client';
import dictionaryParser from '@utils/helpers/dictionary-parser';

const SubmitForm = forwardRef(({ submitHandler, directHandler, onChange, parentName, submitLoading, commentValue, hasScore = false, scoreCallBack, score = 0 }: SubmitFormProps, ref: any) => {
  const dispatch = useDispatch();
  const commentInputRef = useRef<any>(null);
  const { lang } = useParams<{lang : 'fa'|'en'}>();
  const { modules: { comment } } = getDictionary(lang);

  useEffect(() => {
    const loadScriptByURL = (id: string, url: string, callback: any) => {
      const isScriptExist = document.getElementById(id);
      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = function () {
          if (callback) {
            callback();
          }
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) {
        callback();
      }
    };
    loadScriptByURL('recaptcha-key', `https://www.google.com/recaptcha/api.js?render=${process.env.GOOGLE_RECAPTCHA_V3_SITE_KEY}`, function () {
    });
  }, []);

  const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e);
  };

  const handleReCaptchaVerify = () => {
    // e.preventDefault();
    // @ts-ignore
    window?.grecaptcha.ready(() => {
      // @ts-ignore
      window?.grecaptcha.execute(process.env.GOOGLE_RECAPTCHA_V3_SITE_KEY, { action: 'submit' }).then((token: string) => {
        submitCommentHandler(token);
      });
    });
  };

  const submitCommentHandler = (token: string) => {
    if (token) {
      submitHandler({ token });
      if (commentInputRef?.current) {
        commentInputRef.current.value = '';
      }
    } else {
      dispatch(AlertActions.showAlert({ severity: 'danger', text: comment.messages.token_error }));
    }
  };

  const clickDirectHandler = () => {
    directHandler({ id: '', name: '' });
  };

  const handleRatingChange = (e: number) => {
    if (scoreCallBack) {
      scoreCallBack(e);
    }
  };

  return (
    <Div ref={ref} className={'flex-col w-full gap-2 sm:gap-4'}>
      <Text align={'start'} typography={'lg'} className={'py-1 sm:py-3'} type={'bold'}>{comment.title}</Text>
      {parentName !== '' ? (
        <Div className={'items-center gap-x-4 py-4'}>
          <Text
            typography={'sm'}
            color={'grey.700'}
            align={'start'}>
            {dictionaryParser({ content: comment.answer_comment, variables: [{ name: parentName }] })}
          </Text>
          <Chip
            onClick={clickDirectHandler}
            onDelete={clickDirectHandler}
            value={comment.delete}
            color={'red'}
            size={'small'}
          />
        </Div>
      ) : null}
      <TextField
        ref={commentInputRef}
        onChange={changeHandler}
        label={comment.text_fields.label}
        startAdornment={<CommentIcon/>}
        placeholder={comment.text_fields.placeholder}
        inputClassName={'!h-36'}
        multiline={true}
        hasCounter={true}
        maxLength={500}
        disabled={submitLoading}
        value={commentValue}
      />
      <Div className={'justify-end items-end gap-8 flex-col'}>
        {hasScore && !parentName ? (
          <Div className={'gap-4 items-center justify-center'}>
            <Text typography={'sm'} type={'bold'}>
              {comment.your_rate}
            </Text>
            <Rating
              direction={'rtl'}
              onChange={handleRatingChange}
              value={score}
              size={'large'}
            />
          </Div>
        ) : null}
        <Button
          loading={submitLoading}
          disabled={submitLoading || commentValue === ''}
          onClick={handleReCaptchaVerify}
          fontType={'bold'}
          className={'w-44 sm:w-40 md:w-[156px]'}
        >
          {comment.buttons.submit_comment}
        </Button>
      </Div>
    </Div>
  );
});

SubmitForm.displayName = 'SubmitForm';

export default SubmitForm;
